/**
 * KnowledgeGraphPage — the main authenticated user surface.
 * Extracted from App.js in SPD v5.53 Phase D (renamed from KnowledgeGraphApp
 * for consistency with KnowledgeAtlasPage + KnowledgeControlsPage).
 *
 * This is a byte-verbatim move of the pre-Phase-D `KnowledgeGraphApp`.
 * ALL crowdfunding-era UI hidden behind feature flags (Counter demo,
 * DemoDownloadCoachMark, ForecastPanel, walkthrough, CC hints, session exit
 * banner) is preserved verbatim — flip the corresponding SHOW_* flag in .env
 * to re-enable.
 *
 * Props: { onLogout } — invoked when the user clicks the Log-out affordance.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Turnstile from 'react-turnstile';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Upload, Search, Database, FileText, Zap, Download, LogOut, Lock, User, Menu, Settings, ClipboardList, SlidersHorizontal, BarChart2, TrendingUp, AlertTriangle, CheckCircle, XCircle, List, Circle, LayoutGrid, MapPin, Globe, ArrowLeftRight, Store, ShoppingCart, ChevronDown, ChevronUp, ExternalLink, Shield, Image as ImageIcon, Video, Play, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { brandConfig, applyBrandColors, applyBrandFont } from '../config/branding';
import VoiceActivation from '../components/VoiceActivation';
import { useIsMobile } from '../hooks/useIsMobile';

// Extracted Components
import { SourcesSection } from '../components/app/SourcesSection';
import { WebResultsSection } from '../components/app/WebResultsSection';
import { QuietModeChip } from '../components/QuietModeChip';
// Phase E extractions (2026-02-07) — three self-contained modals lifted out
// of KGPage; parent still owns the state, sub-components are pure leaves.
import PolicyModals from '../components/app/PolicyModals';
import ReindexPromptModal from '../components/app/ReindexPromptModal';
import ImageLightbox from '../components/app/ImageLightbox';
import {
  classifyCommand as classifyQuietCommand,
  getQuietMode,
  setQuietMode as persistQuietMode,
  hasSeenFirstTimeTip as hasSeenQuietTip,
  markFirstTimeTipSeen as markQuietTipSeen,
  COPY as QUIET_COPY,
} from '../lib/quietMode';
import { CitationModal } from '../components/app/CitationModal';
// SPD v5.86 (2026-02-14) · FollowUpInput.jsx retired. Its role at the
// bottom of the thread is now taken by <QueryInputRow /> — see import
// on the next line.
import { QueryInputRow } from '../components/app/QueryInputRow';
import { SuggestionsPanel } from '../components/app/SuggestionsPanel';
import { Walkthrough } from '../components/app/Walkthrough';
import { walkthroughCopy } from '../content/walkthrough';
import { RestoreTooltipDot } from '../components/app/RestoreTooltipDot';
import { ControlCentre } from '../components/app/ControlCentre';
import { MenuOverlay } from '../components/app/MenuOverlay';
import { OverlayStack } from '../components/app/OverlayStack';
import { SafeBrowser } from '../components/app/SafeBrowser';
import { QuotaDemoBox } from '../components/app/QuotaDemoBox';
import { SHOW_WALKTHROUGH, SHOW_DEMO_COUNTER, SHOW_CC_HINTS, SHOW_BETA_BANNERS } from '../featureFlags';
import { FloatingTopIcons } from '../components/app/FloatingTopIcons';
import { SessionExitBanner } from '../components/app/SessionExitBanner';
import { DemoDownloadCoachMark } from '../components/app/DemoDownloadCoachMark';
import { trackEvent } from '../utils/telemetry';

// Auth + citation helpers moved to utils/ in SPD v5.53 Phase D.
import { BACKEND_URL, API, getToken, setToken, clearToken, authAxios } from '../utils/authClient';
import { CITATION_RE, renderCitedChildren } from '../utils/citations';
import { classifyIntent, shouldShowChipSurface } from '../lib/queryIntent';
import { useTypeahead } from '../hooks/useTypeahead';
import { useCompletions } from '../hooks/useCompletions';
import { recordSubmittedQuery } from '../lib/completionsCorpus';
import { decodeExcerpt } from '../lib/htmlDecode';
import { buildSourcesMarkdown, buildWordExportPayload } from '../lib/wordExport';
import { markdownComponents, renderContentWithCitations as renderCitationsBase } from '../lib/markdownRender';
import { TypeaheadDropdown } from '../components/app/TypeaheadDropdown';
import { StatsDropdown } from '../components/app/StatsDropdown';
import { CompletionsSurface } from '../components/app/CompletionsSurface';
import { KgHeader } from '../components/app/KgHeader';
import { KgQueryPanel } from '../components/app/KgQueryPanel';
import { DemoIntroModal } from '../components/app/DemoIntroModal';
import { UploadPanel } from '../components/app/UploadPanel';
import { ForecastResultCard } from '../components/app/ForecastResultCard';
import { WebSourcesBlock } from '../components/app/WebSourcesBlock';
import { AnswerContentBlock } from '../components/app/AnswerContentBlock';
import { AnswerFeedbackPanel } from '../components/app/AnswerFeedbackPanel';
import { ProductSearchResults } from '../components/app/ProductSearchResults';
import { QueryThreadItem } from '../components/app/QueryThreadItem';
import { BackToBottomButton } from '../components/app/BackToBottomButton';
import { useUploadFlow } from '../hooks/useUploadFlow';
import { useQueryRefinement } from '../hooks/useQueryRefinement';
import { useQueryOrchestrator } from '../hooks/useQueryOrchestrator';
import useMediaTabs from '../hooks/useMediaTabs';
import useAnswerFeedback from '../hooks/useAnswerFeedback';
import useForecastFlow from '../hooks/useForecastFlow';
import { useQuotaExitBanner } from '../hooks/useQuotaExitBanner';
import { useDemoRunner } from '../hooks/useDemoRunner';


const KnowledgeGraphPage = ({ onLogout }) => {
  const [documents, setDocuments] = useState([]);
  const [entities, setEntities] = useState([]);
  const [stats, setStats] = useState({ documents: 0, entities: 0, relationships: 0 });
  const [query, setQuery] = useState('');
  const [followUpQuery, setFollowUpQuery] = useState('');
  // SPD v5.81 (2026-02-14) — P0 trio-gate for the follow-up input.
  // Result of `classifyIntent(followUpQuery)`, updated in a useEffect
  // below. Consumed by <FollowUpInput> to decide whether to render the
  // Support Local · Big Players · Compare strip. Was previously always
  // rendered — the bug the operator hit in the D9 screenshot.
  const [followUpTransactional, setFollowUpTransactional] = useState(false);
  const [queryThread, setQueryThread] = useState([]);
  const [isQuerying, setIsQuerying] = useState(false);
  // Lever 6 (Jun 3, 2026): quiet mode — user opted out of AI prose synthesis
  // via typed command. Persisted in localStorage by the quietMode helper.
  const [quietMode, setQuietModeState] = useState(() => getQuietMode());
  // KG upload plus reindex flow · state and handlers · extracted 2026-08-01
  // IJ-244 · Phase 5C · see hooks/useUploadFlow.js.
  const {
    selectedFile, setSelectedFile,
    selectedFiles, setSelectedFiles,
    uploadPartition, setUploadPartition,
    batchProgress, setBatchProgress,
    isUploading, setIsUploading,
    reindexPrompt, setReindexPrompt,
    reindexRunning,
    reindexProgress,
    handleInlineForecastUpload,
    handleFileUpload,
    handleBatchUpload,
    handleReindexConfirm,
    handleReindexSkip,
  } = useUploadFlow({
    fetchStats: () => fetchStats(),
    fetchDocuments: () => fetchDocuments(),
    fetchEntities: () => fetchEntities(),
  });

  // Query Refinement (real-time facet prompt) — Session 3.6 (inline thread version)
  // Fires on typing pause ≥1s and on Enter. Shows as a clarification_prompt
  // message inline in the user's thread (no modal overlay).
  //
  // IJ-260 (2026-08-05) — Both `useState` declarations were previously
  // farther down the component body (around line 320). The Phase 5D-1
  // hook extraction (IJ-245) pulled the `useQueryRefinement` call up here
  // but left the state declarations in place, producing a Temporal Dead
  // Zone `ReferenceError: Cannot access 'refineRequestedQueries' before
  // initialization` every render on the authenticated home. Moved both
  // declarations up to precede the hook call. Byte-verbatim on defaults.
  const [refineDebounceTimer, setRefineDebounceTimer] = useState(null);
  const [refineRequestedQueries, setRefineRequestedQueries] = useState(new Set());   // avoid re-prompting identical queries

  // Query refinement flow · five handlers · extracted 2026-08-01
  // IJ-245 · Phase 5D-1 · see hooks/useQueryRefinement.js.
  // handleQuery is the parent-owned mega-handler that stays here until
  // Phase 5D-2 extracts the query orchestrator.
  const {
    fetchRefinement,
    toggleRefineOption,
    submitRefinedQuery,
    advanceRefinePrompt,
    skipRefinePrompt,
  } = useQueryRefinement({
    queryThread,
    setQueryThread,
    setQuery,
    refineRequestedQueries,
    setRefineRequestedQueries,
    handleQuery: (...args) => handleQuery(...args),
  });
  const [showStatsDropdown, setShowStatsDropdown] = useState(false);
  const [showControlCentre, setShowControlCentre] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuView, setMenuView] = useState('main'); // 'main' or 'comparison'

  // Security BRD Beta v1.2.1 §A4 — KG Scope Clamp.
  // At Beta the Knowledge Graph user surface is demo-only; only admin users
  // can upload. We resolve `isAdmin` from /api/auth/verify on mount and use
  // it to hide/disable the Upload UI for non-admin users — they see an
  // "Available at Launch" hover-tag instead.
  // SPD v5.76 (2026-07-12) — D1-defect-5. Same verify call also returns
  // the caller's username; captured into `displayUsername` and threaded
  // through the greeting slots on the KG page + ControlCentre + Menu
  // that previously showed a hardcoded "Maverick". Operator directive:
  // "User name I used to enter the site is 'sbear' NOT Maverick".
  const [isAdmin, setIsAdmin] = useState(false);
  const [displayUsername, setDisplayUsername] = useState('');
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && typeof d.is_admin === 'boolean') setIsAdmin(d.is_admin);
        if (d && typeof d.username === 'string' && d.username.length > 0) {
          setDisplayUsername(d.username);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // KG icon shimmer trigger — increments on login + when user returns to home from a modal.
  // Used as React key to restart the one-shot CSS sweep animation.
  const [kgShimmerKey, setKgShimmerKey] = useState(1);
  const prevModalOpenRef = useRef(false);
  useEffect(() => {
    const isModalOpen = showControlCentre || showMenu;
    // Trigger when transitioning from open -> closed (user returned to home)
    if (prevModalOpenRef.current && !isModalOpen) {
      setKgShimmerKey(k => k + 1);
    }
    prevModalOpenRef.current = isModalOpen;
  }, [showControlCentre, showMenu]);
  
  // Tooltip visibility — persisted in localStorage. EXCEPTION: cc_first_query
  // is treated as session-only (D2.0 follow-up #2, Feb 23, 2026) so the
  // Control Centre nudge resets on every fresh page load alongside
  // controlCentreVisited.
  const [hiddenTooltips, setHiddenTooltips] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('searchinc_hidden_tooltips') || '{}');
      // Drop cc_first_query from the loaded set — never restore it from disk
      const { cc_first_query: _drop, ...rest } = stored;
      return rest;
    } catch { return {}; }
  });
  const handleHideTooltip = (id) => {
    const updated = { ...hiddenTooltips, [id]: true };
    setHiddenTooltips(updated);
    // cc_first_query stays session-only — don't write it to disk
    if (id !== 'cc_first_query') {
      localStorage.setItem('searchinc_hidden_tooltips', JSON.stringify(updated));
    }
  };
  const handleRestoreTooltips = () => {
    setHiddenTooltips({});
    localStorage.removeItem('searchinc_hidden_tooltips');
  };

  const handleRestoreTooltip = (id) => {
    setHiddenTooltips(prev => {
      const next = { ...prev };
      delete next[id];
      try { localStorage.setItem('searchinc_hidden_tooltips', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  // D2.0 follow-up #2 (Feb 23, 2026) — Switched from localStorage to
  // in-memory state so the Control Centre nudge resets on every page
  // refresh. Previous lifetime persistence meant once a user opened CC
  // even once, the callout never reappeared, which (a) made testing
  // confusing and (b) contradicted the user's mental model that refresh
  // clears state. Now: tag shows on the first query of every fresh page
  // load; opens-CC or Don't-Show hides it for the current session only.
  const [controlCentreVisited, setControlCentreVisited] = useState(false);
  const markControlCentreVisited = () => {
    if (!controlCentreVisited) {
      setControlCentreVisited(true);
    }
  };
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  const [forecastResult, setForecastResult] = useState(null);
  const [awaitingForecastFile, setAwaitingForecastFile] = useState(false);
  const [includeFormulas, setIncludeFormulas] = useState(true);  // Default: include formulas
  const [sourcesViewMode, setSourcesViewMode] = useState('circles'); // 'list' or 'circles' - circles is default
  const [webViewMode, setWebViewMode] = useState(window.innerWidth < 640 ? 'list' : 'circles'); // list default on mobile
  const [browseMode, setBrowseMode] = useState('direct'); // 'safety' or 'direct' — default OFF (direct)
  const [controlCentreView, setControlCentreView] = useState('main');
  const [accuracyMode, setAccuracyMode] = useState('manual');
  const [citationModal, setCitationModal] = useState(null); // {filename, excerpt, docId}
  const [activePolicyModal, setActivePolicyModal] = useState(null); // 'ethics', 'terms', 'privacy', 'aiRisk'
  // NOTE (Phase E, 2026-02-07): POLICY_META + the SEO title/meta swap
  // useEffect were moved into components/app/PolicyModals.jsx alongside
  // the modal JSX so the effect lives next to what it drives.
  const [webResultsExpanded, setWebResultsExpanded] = useState(new Set()); // Track expanded web results per thread item
  // Track which product-search panels have been expanded past the
  // first 5 bubbles. Set<idx>. Per-spec: show 5 bubbles, the other 5
  // are expandable via "Show 5 more" toggle (Feb 27, 2026 — T4 fix).
  const [productResultsExpanded, setProductResultsExpanded] = useState(new Set());
  const [safeBrowserState, setSafeBrowserState] = useState(null); // { url, title } or null
  // Multi-modal tabs (Web / Images / Videos) per thread item
  const { mediaTab, setMediaTab, mediaCache, setMediaCache, mediaLoading, setMediaLoading, mediaError, setMediaError } = useMediaTabs();
  const [lightbox, setLightbox] = useState(null); // { src, title, sourceUrl } or null
  // IJ-260 (2026-08-05) — `const api = authAxios();` previously sat
  // ~166 lines further down (post-refactor line 447). Both
  // `useAnswerFeedback` and `useForecastFlow` (IJ-248 and IJ-255) read
  // `api` by direct reference, which threw a Temporal Dead Zone
  // `ReferenceError: Cannot access 'api' before initialization` on
  // every authenticated home render. Moved the declaration up to
  // precede the first consuming hook. Byte-verbatim on the call.
  const api = authAxios();
  // Answer feedback (thumbs up/down) per query_id
  const { feedbackByQid, setFeedbackByQid, commentDraft, setCommentDraft, submitAnswerFeedback } = useAnswerFeedback({ api, API, toast });
  // Forecast handlers (Phase 5F · IJ-255)
  const { handleGenerateForecast, handleDownloadForecast } = useForecastFlow({
    api, API, toast, trackEvent,
    selectedFile, setSelectedFile,
    includeFormulas,
    setIsForecastLoading,
    setForecastResult,
    setAwaitingForecastFile,
  });
  
  // Product/Service Query Intent Detection
  const [queryIntent, setQueryIntent] = useState(null); // 'navigation', 'act', 'information', 'compare', null
  const [detectedSubject, setDetectedSubject] = useState(''); // The product/service being searched
  const [showSourceOptions, setShowSourceOptions] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]); // ['local', 'online', 'compare']
  const [intentTypingTimeout, setIntentTypingTimeout] = useState(null);

  // SPD v5.83 · Phase 2c (2026-07-14) — location-consent inline popup.
  // When location is OFF and the user clicks Support Local (or Compare,
  // which also needs local data), we set this state to open a small
  // Y/N popup: "Do you want to provide us permission to turn on your
  // location?" Y → flip dataControls.location to 'on' and fire the
  // pending search. N → dismiss.
  const [locationConsentPrompt, setLocationConsentPrompt] = useState(null); // { sources, query } | null
  // SPD v5.83 · Phase 2 (2026-07-14) — the T5 "clarify vague query" modal
  // was retired here. The dynamic-primitive typeahead dropdown now covers
  // every narrowing path (Compare primitive on `two_col_compare` layout_hint,
  // Products primitive on `card_grid`, Topic primitive with the LLM-authored
  // intro_line etc.). All state, refs, helpers, and JSX for that modal
  // (`clarifyPrompt`, `clarifyChoice`, `clarifyFreeText`, `clarifySources`,
  // `isQueryVague`, `clarifyOptionsFor`, `handleClarifyContinue`,
  // `handleClarifySkip`) have been deleted. `attemptProductSearch` now
  // proxies straight through to `handleProductSearch`.

  // Transact-intent intercept (Feb 28, 2026, per user) — when the user types a
  // shopping query (e.g. "buy air jordans") and hits Enter WITHOUT selecting a
  // Source-mode chip, we used to fire the KG path which would have Claude give
  // a polite-but-unhelpful "I'm a search engine, not a transaction platform"
  // refusal. The correct UX is to surface the three things the app CAN do for a
  // transactional query (pricing, comparison, tier/model) and let the user pick.
  // Modal copy is user-provided verbatim — do not paraphrase. Buttons map to
  // the same actions the chips trigger.
  // SPD v5.86 (2026-02-14) — transactPrompt state DELETED. The modal
  // it used to open (pricing / comparisons / tier-model buttons) has
  // been retired now that Compare is the assumed default and the
  // fire-on-Enter path already covers all three intents in a single
  // multi-source result.

  // Query Refinement (real-time facet prompt) — Session 3.6 (inline thread version)
  // Fires on typing pause ≥1s and on Enter. Shows as a clarification_prompt
  // message inline in the user's thread (no modal overlay).
  // IJ-260 (2026-08-05) — `useState` declarations moved up above the
  // `useQueryRefinement` hook call to close the TDZ crash.

  const [dataControls, setDataControls] = useState({
    username: 'off',
    location: 'off',
    social: 'off',
    media: 'off',
  });
  const [accuracyScore, setAccuracyScore] = useState(100);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [dataStats, setDataStats] = useState({ totals: { username_data: 0, location: 0, social: 0, media: 0 }, grand_total: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, confirmed: false, deleted: false, timestamp: null });
  const [lastDeletedCount, setLastDeletedCount] = useState(null);
  const [dataStatsExpanded, setDataStatsExpanded] = useState(true);
  const [qaData, setQaData] = useState({ summary: null, history: [], loading: false });
  const [showQaPanel, setShowQaPanel] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  
  // Phase 5: Geolocation state
  const [userLocation, setUserLocation] = useState(null); // { lat, lng, city, region, display }
  const [geoLoading, setGeoLoading] = useState(false);
  
  const queryTextareaRef = useRef(null);
  const followUpRef = useRef(null);
  const threadEndRef = useRef(null);
  const handleQueryRef = useRef(null);
  const topIconsRowRef = useRef(null);
  const downloadForecastBtnAnchorRef = useRef(null);
  const isMobile = useIsMobile(640);
  const isTabletOrBelow = useIsMobile(1024);

  // Scroll the user to the result they just triggered.
  //  Always scroll to the actual thread-item DOM node (idx 0 included).
  //  This drops the user's viewport down to the loading state and result —
  //  away from the welcome hero — so the engine's work is the primary view
  //  rather than the user remaining stranded at the top of the page.
  const scrollToThreadItem = (idx) => {
    setTimeout(() => {
      const target = idx == null ? 0 : idx;
      const node = document.querySelector(`[data-testid="thread-item-${target}"]`);
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback only if the node isn't mounted yet (rare).
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  // Voice command handler - processes commands from "Hey SI"
  const handleVoiceCommand = useCallback((command) => {
    console.log('Voice command received:', command);
    toast.success(`Voice command: "${command}"`, { duration: 2000 });
    
    // Close control centre if open
    setShowControlCentre(false);
    
    // Set the query and trigger search
    setQuery(command);
    
    // Trigger the query after a short delay to allow state update
    setTimeout(() => {
      if (handleQueryRef.current) {
        handleQueryRef.current(command, false);
      }
    }, 100);
  }, []);

  useEffect(() => {
    applyBrandColors(brandConfig.colors);
    applyBrandFont(brandConfig.fonts.primary);
    fetchStats();
    fetchDocuments();
    fetchEntities();
    // Auto-focus the query textarea when app loads
    setTimeout(() => {
      if (queryTextareaRef.current) {
        queryTextareaRef.current.focus();
      }
    }, 100);
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
  }, []);

  // Global keydown: focus a query textarea when the user starts typing
  // anywhere on the page. When a result thread already exists, we route
  // focus to the follow-up textarea (not the top one) so keystrokes don't
  // yank the user back to the first query. `preventScroll: true` keeps the
  // viewport still so focusing doesn't cause a jarring jump to the top.
  useEffect(() => {
    const handleGlobalKey = (e) => {
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const active = document.activeElement;
        const tag = active && active.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
          const target =
            queryThread.length > 0 && followUpRef.current
              ? followUpRef.current
              : queryTextareaRef.current;
          if (target && !showControlCentre) {
            try {
              target.focus({ preventScroll: true });
            } catch {
              target.focus();
            }
          }
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKey);
    return () => document.removeEventListener('keydown', handleGlobalKey);
  }, [showControlCentre, queryThread.length]);

  // IJ-260 (2026-08-05) — `const api = authAxios();` moved up next to
  // `useAnswerFeedback` / `useForecastFlow` to close the TDZ crash.

  // Phase 5: Get user location — tries browser GPS first, then server-side IP geolocation (always works)
  const requestGeolocation = useCallback(() => {
    return new Promise((resolve) => {
      setGeoLoading(true);
      
      // Server-side IP geolocation fallback (most reliable — uses user's real IP via proxy headers)
      const serverGeoFallback = async () => {
        try {
          const token = localStorage.getItem('startech_token');
          const resp = await fetch(`${API}/geolocation`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await resp.json();
          if (data && data.city) {
            const loc = { ...data, source: 'ip' };
            console.log('Server IP geolocation:', loc.display);
            setUserLocation(loc);
            setGeoLoading(false);
            resolve(loc);
            return;
          }
        } catch (e) {
          console.warn('Server geolocation failed:', e);
        }
        setGeoLoading(false);
        resolve(null);
      };
      
      // Try browser geolocation first (most accurate — GPS/WiFi)
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              // zoom=16 gives neighbourhood-level detail (suburb, district)
              const resp = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=16&addressdetails=1`,
                { headers: { 'Accept-Language': 'en', 'User-Agent': 'SearchInc/1.0' } }
              );
              const geo = await resp.json();
              const addr = geo.address || {};
              // Extract most specific area name: suburb > neighbourhood > city_district > city
              const suburb = addr.suburb || addr.neighbourhood || addr.city_district || '';
              const city = addr.city || addr.town || addr.village || addr.municipality || '';
              const region = addr.state || addr.province || addr.county || '';
              const country = addr.country || '';
              // Display uses the most specific name first
              const specificArea = suburb || city;
              const display = suburb && city && suburb !== city
                ? `${suburb}, ${city}, ${region}`.replace(/, $/, '')
                : [city, region].filter(Boolean).join(', ');
              const loc = {
                lat: latitude, lng: longitude,
                suburb, city, region, country,
                display,
                source: 'gps',
              };
              console.log('GPS geolocation:', loc.display, `(suburb=${suburb}, city=${city})`);
              setUserLocation(loc);
              setGeoLoading(false);
              resolve(loc);
            } catch (e) {
              console.warn('Reverse geocode failed, using server IP fallback');
              await serverGeoFallback();
            }
          },
          async () => {
            // Browser geolocation denied/failed — use server IP fallback
            console.warn('Browser geolocation denied, using server IP fallback');
            await serverGeoFallback();
          },
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
        );
      } else {
        serverGeoFallback();
      }
    });
  }, []);

  const fetchStats = async () => {
    try { const r = await api.get(`${API}/graph/stats`); setStats(r.data); } catch (e) { console.error(e); }
  };
  const fetchDocuments = async () => {
    try { const r = await api.get(`${API}/documents`); setDocuments(r.data); } catch (e) { console.error(e); }
  };
  const fetchEntities = async () => {
    try { const r = await api.get(`${API}/entities`); setEntities(r.data); } catch (e) { console.error(e); }
  };

  // Multi-modal media fetchers (Images / Videos). Lazy loaded per thread idx & cached.
  const fetchMediaResults = async (idx, modality, query) => {
    if (!query || (mediaCache[idx] && mediaCache[idx][modality])) return;
    setMediaLoading(prev => ({ ...prev, [idx]: modality }));
    setMediaError(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [modality]: null } }));
    try {
      const r = await api.post(`${API}/search/${modality}`, { query, max_results: modality === 'images' ? 24 : 12 });
      setMediaCache(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [modality]: r.data.results || [] } }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load results';
      setMediaError(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [modality]: msg } }));
      setMediaCache(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [modality]: [] } }));
    } finally {
      setMediaLoading(prev => ({ ...prev, [idx]: null }));
    }
  };

  const switchMediaTab = (idx, tab, query) => {
    setMediaTab(prev => ({ ...prev, [idx]: tab }));
    if ((tab === 'images' || tab === 'videos') && (!mediaCache[idx] || !mediaCache[idx][tab])) {
      fetchMediaResults(idx, tab, query);
    }
  };

  // Answer feedback — thumbs up/down on final answers
  // Extracted to hooks/useAnswerFeedback.js in Phase 5E-2 (IJ-248).

  // Load user preferences from backend
  const loadUserPreferences = async () => {
    try {
      const r = await api.get(`${API}/user/preferences`);
      const prefs = r.data.preferences;
      setDataControls({
        username: prefs.username_data || 'off',
        location: prefs.location || 'off',
        social: prefs.social || 'off',
        media: prefs.media || 'off',
      });
      setAccuracyMode(prefs.accuracy_mode || 'manual');
      setAccuracyScore(r.data.accuracy_score);
      setPreferencesLoaded(true);
    } catch (e) {
      console.error('Failed to load preferences:', e);
      setPreferencesLoaded(true);
    }
  };

  // Fetch data stats (count of data points)
  const fetchDataStats = async () => {
    try {
      const r = await api.get(`${API}/user/data_points`);
      setDataStats({ 
        totals: r.data, 
        grand_total: (r.data.username_data || 0) + (r.data.location || 0) + (r.data.social || 0) + (r.data.media || 0)
      });
    } catch (e) {
      console.error('Failed to fetch data stats:', e);
    }
  };

  // Log search activity (called when user performs a search)
  const logSearchActivity = async (query) => {
    try {
      await api.post(`${API}/user/log_search`, { query });
      // Refresh data stats after logging
      fetchDataStats();
    } catch (e) {
      console.error('Failed to log search:', e);
    }
  };

  // Log location activity
  const logLocationActivity = async () => {
    try {
      await api.post(`${API}/user/log_location`);
      fetchDataStats();
    } catch (e) {
      console.error('Failed to log location:', e);
    }
  };

  // Log media search activity
  const logMediaActivity = async () => {
    try {
      await api.post(`${API}/user/log_media`);
      fetchDataStats();
    } catch (e) {
      console.error('Failed to log media:', e);
    }
  };

  // Fetch QA Summary
  const fetchQaSummary = async () => {
    try {
      setQaData(prev => ({ ...prev, loading: true }));
      const [summaryRes, historyRes] = await Promise.all([
        api.get(`${API}/qa/summary`),
        api.get(`${API}/qa/history?limit=5`)
      ]);
      setQaData({
        summary: summaryRes.data,
        history: historyRes.data.reports || [],
        loading: false
      });
    } catch (e) {
      console.error('Failed to fetch QA data:', e);
      setQaData(prev => ({ ...prev, loading: false }));
    }
  };

  // Run manual QA check
  const runManualQaCheck = async () => {
    try {
      setQaData(prev => ({ ...prev, loading: true }));
      await api.post(`${API}/qa/run-check`);
      await fetchQaSummary();
      toast.success('QA check completed');
    } catch (e) {
      console.error('Failed to run QA check:', e);
      toast.error('QA check failed');
      setQaData(prev => ({ ...prev, loading: false }));
    }
  };

  // Save user preferences to backend
  const saveUserPreferences = async (newControls, newMode) => {
    try {
      const r = await api.post(`${API}/user/preferences`, {
        username_data: newControls.username,
        location: newControls.location,
        social: newControls.social,
        media: newControls.media,
        accuracy_mode: newMode || accuracyMode,
      });
      setAccuracyScore(r.data.accuracy_score);
      
      // If any control was set to 'delete', show results and refresh stats
      const deletedTypes = [];
      if (newControls.username === 'delete') deletedTypes.push('Username Data');
      if (newControls.location === 'delete') deletedTypes.push('Location Data');
      if (newControls.social === 'delete') deletedTypes.push('Social Media Data');
      if (newControls.media === 'delete') deletedTypes.push('Media Search Data');
      
      if (deletedTypes.length > 0) {
        // Calculate total deleted from the response
        const deleted = r.data.deleted || {};
        let totalDeleted = 0;
        Object.values(deleted).forEach(typeData => {
          if (typeof typeData === 'object') {
            totalDeleted += Object.values(typeData).reduce((a, b) => a + b, 0);
          }
        });
        
        setLastDeletedCount(totalDeleted);
        toast.success(`${deletedTypes.join(', ')} permanently deleted (${totalDeleted} records)`);
        
        // Reset deleted controls to 'off' in state
        setDataControls({
          username: newControls.username === 'delete' ? 'off' : newControls.username,
          location: newControls.location === 'delete' ? 'off' : newControls.location,
          social: newControls.social === 'delete' ? 'off' : newControls.social,
          media: newControls.media === 'delete' ? 'off' : newControls.media,
        });
        
        // Refresh data stats
        fetchDataStats();
      }
      
      // Reset delete confirmation
      setDeleteConfirm({ show: false, type: null, confirmed: false });
    } catch (e) {
      console.error('Failed to save preferences:', e);
      toast.error('Failed to save preferences');
    }
  };

  // Handle delete button click - show confirmation
  const handleDeleteClick = (type) => {
    setDeleteConfirm({ show: true, type, confirmed: false });
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    if (deleteConfirm.confirmed && deleteConfirm.type) {
      try {
        // Map control key to API collection name
        const collectionMap = {
          'username': 'search_logs',
          'location': 'location_logs',
          'social': 'social_activity',
          'media': 'media_search_logs'
        };
        
        const collection = collectionMap[deleteConfirm.type];
        if (collection) {
          // Call delete API
          const response = await api.delete(`${API}/user/data/${collection}`);
          const deletedCount = response.data.deleted_count || 0;
          const pdfUrl = response.data.pdf_url || null;
          const deletionId = response.data.deletion_id || null;
          setLastDeletedCount(deletedCount);
          
          // Generate timestamp
          const now = new Date();
          const timestamp = now.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          
          // Update control to 'off' after deletion (directly, no saveUserPreferences)
          const deletedType = deleteConfirm.type;
          const newControls = { ...dataControls, [deletedType]: 'off' };
          setDataControls(newControls);
          
          // Save preferences silently without resetting deleteConfirm
          try {
            const r = await api.post(`${API}/user/preferences`, {
              username_data: newControls.username,
              location: newControls.location,
              social: newControls.social,
              media: newControls.media,
              accuracy_mode: accuracyMode,
            });
            setAccuracyScore(r.data.accuracy_score);
          } catch (e) {
            console.error('Failed to save preferences after delete:', e);
          }
          
          // Refresh data stats to show 0
          fetchDataStats();
          
          // Show deletion confirmation with timestamp (AFTER all other updates)
          setDeleteConfirm({ 
            show: true, 
            type: deletedType, 
            confirmed: true,
            deleted: true, 
            timestamp: timestamp,
            pdfUrl: pdfUrl,
            deletionId: deletionId
          });
        }
      } catch (e) {
        console.error('Failed to delete data:', e);
        toast.error('Failed to delete data');
        setDeleteConfirm({ show: false, type: null, confirmed: false, deleted: false });
      }
    }
  };

  // Phase 9 — scrub receipt state (returned by /api/account/delete-me)
  const [scrubReceipt, setScrubReceipt] = useState(null);

  // Execute "Go Nuclear" - delete ALL user data via the new Phase 9 endpoint
  // which both CASCADES through all 10 user-keyed collections AND returns a
  // cryptographically-signed scrub receipt the user can download as PDF.
  const executeGoNuclear = async () => {
    try {
      const resp = await api.post(`${API}/account/delete-me`);
      // Capture the receipt so the next view can render it. The session
      // token is server-side-revoked by /delete-me; we keep it locally for
      // the few seconds the receipt panel is up so the user can fetch the
      // PDF. After they click "I have the receipt", we hard-logout.
      setScrubReceipt(resp.data);
      setDataControls({ username: 'off', location: 'off', social: 'off', media: 'off' });
      setAccuracyScore(0);
      // View transition to nuclear-receipt screen handled by ControlCentre
    } catch (e) {
      console.error('Failed to execute nuclear option:', e);
      toast.error('Failed to delete data');
      throw e;
    }
  };

  // Query orchestrator · eight handlers · extracted 2026-08-01
  // IJ-246 · Phase 5D-2 · see hooks/useQueryOrchestrator.js.
  const {
    handleQuery,
    handlePermissionResponse,
    detectQueryIntent,
    handleQueryInputChange,
    toggleSourceSelection,
    attemptProductSearch,
    submitPrimaryQuery,
    handleProductSearch,
  } = useQueryOrchestrator({
    query, queryThread, quietMode, dataControls, selectedSources, queryIntent,
    detectedSubject, hasSeenQuietTip: hasSeenQuietTip(), firstTimeTip: null, followUpQuery,
    showSourceOptions, userLocation,
    intentTypingTimeout, refineDebounceTimer, refineRequestedQueries,
    setQuery, setQueryThread, setIsQuerying, setQueryIntent,
    setDetectedSubject, setFollowUpQuery, setAwaitingForecastFile,
    setQuietModeState, setSelectedSources, setShowSourceOptions,
    setDataControls, setAccuracyScore,
    setIntentTypingTimeout, setRefineDebounceTimer,
    setLocationConsentPrompt,
    accuracyMode,
    followUpRef, queryTextareaRef, handleQueryRef,
    // IJ-260 (2026-08-05) — wrap the three functions whose definitions sit
    // farther down the component body (`handleQuotaExceededError` at ~L1128,
    // `handleExportThread` at ~L1403, `incrementBilledCount` at ~L1120)
    // in lazy arrow closures so the reference resolves at CALL time (safe)
    // rather than at hook-init time (TDZ crash on every authed home render).
    // Same lazy pattern used above for `handleQuery` in `useQueryRefinement`.
    handleQuotaExceededError: (...a) => handleQuotaExceededError(...a),
    handleExportThread: (...a) => handleExportThread(...a),
    incrementBilledCount: (...a) => incrementBilledCount(...a),
    logSearchActivity, logMediaActivity,
    logLocationActivity, scrollToThreadItem, requestGeolocation,
    quietOnToast: QUIET_COPY.quietOnToast,
    quietOffToast: QUIET_COPY.quietOffToast,
    fetchRefinement,
  });

  // IJ-269 · Word brief export sources fix (2026-08-07)
  // Build a markdown Sources section from a thread-item result. The
  // backend `/export/word` route only receives a single `content`
  // string, so the sources have to travel inside that string. Prefers
  // `result.sources` (brief-shape · { id, label }) when present, then
  // falls back to `result.web_results` (regular query-shape ·
  // { title, url, source_name, snippet, domain }). Returns "" when no
  // sources exist.
  const handleExportToWord = async (threadItem) => {
    const payload = buildWordExportPayload(threadItem);
    if (!payload) { toast.error('No results to export'); return; }
    try {
      const response = await api.post(`${API}/export/word`, {
        content: payload.content,
        title: payload.title,
        version: payload.version,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', payload.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to export document');
    }
  };

  // Download original source document
  const handleDownloadSourceDocument = async (doc) => {
    if (!doc.id) {
      toast.error('Document ID not available');
      return;
    }
    try {
      const response = await api.get(`${API}/documents/download/${doc.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${doc.filename}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Original file not available for download');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  // Handle citation click - show modal with source excerpt
  const handleCitationClick = (citationNum, citationMap) => {
    const citation = citationMap[citationNum];
    if (citation) {
      setCitationModal({
        filename: citation.filename,
        excerpt: citation.excerpt,
        docId: citation.id
      });
    }
  };

  // Render content with clickable citations.
  // Accepts any of: [[n]], [n], [[[n]]], and renders a single pill showing n.
  // This guards against LLM output drift where nesting or single brackets leak
  // through, which previously caused visible duplicate brackets like [[1]].
  const renderContentWithCitations = (content, citationMap) =>
    renderCitationsBase(content, citationMap, handleCitationClick);

  // Forecast handlers extracted to hooks/useForecastFlow.js in Phase 5F (IJ-255)



  // ── P&L Demo (pre-loaded sample budget — does NOT decrement query quota) ──
  // showDemoIntro, the download coach-mark cluster and handleRunDemo moved to
  // hooks/useDemoRunner.js (2026-09-02 · IJ-464 · Phase 5.5b). The hook call
  // sits just below useQuotaExitBanner so it can read demoViewed/setDemoViewed.
  const {
    billedCount,
    incrementBilledCount,
    handleQuotaExceededError,
    demoViewed,
    setDemoViewed,
    exitBannerVariant,
    setExitBannerVariant,
    gracePctRemaining,
    handleExitClick,
    bannerDismissedRef,
    sessionStartRef,
    dismissalTokenRef,
  } = useQuotaExitBanner({ api, API, isAdmin, onLogout });
  const {
    showDemoIntro,
    setShowDemoIntro,
    showDownloadCoachMark,
    setShowDownloadCoachMark,
    forecastTooltip,
    setForecastTooltip,
    handleRunDemo,
  } = useDemoRunner({
    api, API, setQuery, setQueryThread, setIsForecastLoading,
    setForecastResult, forecastResult, setMediaCache, setMediaTab,
    setMediaLoading, setMediaError, followUpRef, demoViewed, setDemoViewed,
  });




  const StatusBadge = ({ status, varianceAmount }) => {
    // Floating-point dust guard: any variance within $1 of zero is on-plan, regardless of upstream status
    const v = varianceAmount != null ? Number(varianceAmount) : null;
    if (v != null && Math.abs(v) < 1) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/80 text-white text-xs font-bold"><CheckCircle className="h-3 w-3" />ON PLAN</span>;
    }
    // If green and variance is negative (under budget), show SURPLUS
    if (status === 'green' && v != null && v < 0) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/80 text-white text-xs font-bold"><CheckCircle className="h-3 w-3" />SURPLUS</span>;
    }
    if (status === 'green') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/80 text-white text-xs font-bold"><CheckCircle className="h-3 w-3" />ON PLAN</span>;
    if (status === 'yellow') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-600/80 text-white text-xs font-bold"><AlertTriangle className="h-3 w-3" />WATCH</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/80 text-white text-xs font-bold"><XCircle className="h-3 w-3" />AT RISK</span>;
  };

  const formatCurrency = (val) => {
    if (val == null) return '—';
    const num = Number(val);
    if (isNaN(num)) return '—';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(num);
  };

  const handleExportThread = async () => {
    if (queryThread.length === 0) { toast.error('No thread to export'); return; }
    try {
      // Combine all thread items into one document with version markers
      const parts = queryThread.map((item, idx) => {
        const version = item.result.version ? ` — ${item.result.version}` : '';
        const separator = idx > 0 ? '\n\n---\n\n' : '';
        return `${separator}**Query:** ${item.query}\n\n${item.result.answer}${buildSourcesMarkdown(item.result)}`;
      });
      const fullContent = parts.join('');
      const response = await api.post(`${API}/export/word`, { content: fullContent, title: 'Full Thread Export' }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Thread_Export_${new Date().toISOString().split('T')[0]}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Full thread exported');
    } catch (error) {
      toast.error('Failed to export thread');
    }
  };


  // Video autoplay fix
  useEffect(() => {
    const video = document.querySelector('video');
    if (video) {
      video.play().catch(e => console.log('Video autoplay blocked:', e));
    }
  }, []);

  // Auto-resize follow-up textarea when value changes programmatically
  useEffect(() => {
    if (followUpRef.current && followUpQuery) {
      followUpRef.current.style.height = 'auto';
      followUpRef.current.style.height = followUpRef.current.scrollHeight + 'px';
    }
  }, [followUpQuery]);

  // SPD v5.82 (2026-02-14) — LLM-powered predictive typeahead.
  // The `useTypeahead` hook debounces 300 ms then hits
  // POST /api/search/typeahead. It returns the 3 predicted completions,
  // an intent-confidence score (0-1), and any ambiguity axes the
  // classifier flagged for the clarification-chip row.
  //
  // We run TWO independent instances — one bound to the primary
  // `query` field and one bound to `followUpQuery` — so both surfaces
  // get their own dropdown + trio-gate signal.
  const primaryTA = useTypeahead(query);
  const followUpTA = useTypeahead(followUpQuery);

  // SPD v5.87 (2026-02-14) · Predictive Completions — client-side
  // trie ranker + Session Query Refinement Memory. Sub-100 ms hard
  // cap on first paint; backend enrichment (KA concepts) upgrades
  // the trie on first mount and is cached in sessionStorage for the
  // rest of the tab lifetime. `consent.username` gates the refinement-
  // memory boost (Google-gap fix — completions bias toward the recent-
  // refinement chain when the user has opted in).
  const _completionsConsent = React.useMemo(() => ({
    username: dataControls?.username === 'on',
    personalisation: dataControls?.username === 'on',
  }), [dataControls?.username]);
  const primaryCompletions = useCompletions(query, { consent: _completionsConsent });
  const followUpCompletions = useCompletions(followUpQuery, { consent: _completionsConsent });

  // SPD v5.81 (2026-02-14) — P0 trio-gate for the follow-up input.
  // Runs the shared `classifyIntent` on every follow-up keystroke and
  // stores the transactional verdict for <FollowUpInput> to consume.
  // Empty input → hidden. Non-transactional (info / navigation-only
  // without shopping intent) → hidden. Only surfaces the trio strip
  // once confidence ≥ MIN_TRANSACT_CONFIDENCE.
  //
  // SPD v5.82 update — the local regex verdict is now the LOWER-BOUND.
  // If the LLM classifier returns intent_confidence ≥ 0.5 we honour
  // that too, so bare category nouns like "shoes" now correctly arm
  // the trio (regex alone would miss them).
  useEffect(() => {
    const { isTransactional } = classifyIntent(followUpQuery);
    const llmConfident = (followUpTA?.intent_confidence || 0) >= 0.5;
    setFollowUpTransactional(isTransactional || llmConfident);
  }, [followUpQuery, followUpTA?.intent_confidence]);

  // SPD v5.82 (2026-02-14) — SINGLE authority for the primary trio-gate.
  //
  // Fires on every keystroke (query change) and every LLM verdict
  // (primaryTA.intent_confidence change). Combines the local-regex
  // and LLM-classifier signals — arms the trio when EITHER says the
  // query is transactional. Retracts when both say otherwise, so the
  // trio demotes cleanly when the user switches from a commerce query
  // to an informational one.
  //
  // detectQueryIntent (called from the 500 ms local debounce) NO
  // LONGER writes to showSourceOptions/detectedSubject — it only
  // updates `queryIntent` for downstream flows. This eliminates the
  // race we saw on preview where the ~4 s LLM roundtrip's verdict
  // could be overwritten by the 500 ms debounce firing after it.
  useEffect(() => {
    const conf = primaryTA?.intent_confidence || 0;
    const { isTransactional: localSaysYes, subject: localSubject } = classifyIntent(query);
    const trimmed = (query || '').trim();
    if (trimmed.length === 0) {
      setShowSourceOptions(false);
      setDetectedSubject('');
      return;
    }
    if (conf >= 0.5 || localSaysYes) {
      setDetectedSubject(localSubject || trimmed);
      setShowSourceOptions(true);
    } else {
      setShowSourceOptions(false);
      setDetectedSubject('');
    }
  }, [primaryTA?.intent_confidence, query]);

  // D4 fix (Jun 11 2026) — Auto-resize the main query textarea to match the
  // wrapped <span> behind it. Without this, `rows={1}` + `overflow-hidden`
  // forced the textarea to one line while the span wrapped to 2+ lines, so
  // any transparent-text leak from the textarea ended up horizontally
  // scroll-offset from the span — visible as duplicated/overlapping glyphs
  // on Edge/Chromium. Matching heights aligns the (transparent) textarea
  // text directly underneath the span, so even if any glyph leaks, it
  // sits pixel-perfect on top of the visible text instead of beside it.
  useEffect(() => {
    if (queryTextareaRef.current) {
      queryTextareaRef.current.style.height = 'auto';
      queryTextareaRef.current.style.height = queryTextareaRef.current.scrollHeight + 'px';
    }
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden flex flex-col">
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed top-0 left-0 w-full h-screen object-cover z-0"
        onCanPlay={(e) => e.target.play()}
      >
        <source src="/assets/Wave_Dots.mp4" type="video/mp4" />
      </video>
      
      <Toaster richColors position="top-right" />
      
      {/* Voice Activation - Always listening for "Hey SI" */}
      <VoiceActivation 
        onCommand={handleVoiceCommand} 
        isEnabled={voiceEnabled} 
      />
      
      {/* Header · extracted 2026-08-01 · IJ-243 · Phase 5B · components/app/KgHeader.jsx */}
      <KgHeader
        isMobile={isMobile}
        hiddenTooltips={hiddenTooltips}
        onHideTooltip={handleHideTooltip}
        onRestoreTooltip={handleRestoreTooltip}
        isAdmin={isAdmin}
        kgShimmerKey={kgShimmerKey}
        displayUsername={displayUsername}
        onExitClick={handleExitClick}
        onOpenComparison={() => { setShowMenu(true); setMenuView('comparison'); }}
      />

      {/* Stats dropdown · extracted 2026-09-02 · IJ-465 · Phase 5.5c · components/app/StatsDropdown.jsx */}
      <StatsDropdown
        showStatsDropdown={showStatsDropdown}
        setShowStatsDropdown={setShowStatsDropdown}
        stats={stats}
      />

      {/* Main Content - full width, no horizontal padding for edge-to-edge icons */}
      <div className="flex-1 w-full py-8 relative z-10">
        <div className="w-full">
          
          {/* KG upload flow · hidden file-input plus batch/single-file panels ·
              extracted 2026-08-01 · IJ-244 · Phase 5C · components/app/UploadPanel.jsx */}
          <UploadPanel
            isAdmin={isAdmin}
            selectedFile={selectedFile}
            selectedFiles={selectedFiles}
            uploadPartition={uploadPartition}
            batchProgress={batchProgress}
            isUploading={isUploading}
            awaitingForecastFile={awaitingForecastFile}
            isForecastLoading={isForecastLoading}
            setSelectedFile={setSelectedFile}
            setSelectedFiles={setSelectedFiles}
            setUploadPartition={setUploadPartition}
            setBatchProgress={setBatchProgress}
            onFileUpload={handleFileUpload}
            onBatchUpload={handleBatchUpload}
          />

          {/* Top Query Input · extracted 2026-09-01 · IJ-459 · Phase 5A · components/app/KgQueryPanel.jsx */}
          <KgQueryPanel
            isMobile={isMobile}
            topIconsRowRef={topIconsRowRef}
            markControlCentreVisited={markControlCentreVisited}
            setShowControlCentre={setShowControlCentre}
            setControlCentreView={setControlCentreView}
            fetchDataStats={fetchDataStats}
            isQuerying={isQuerying}
            queryThread={queryThread}
            controlCentreVisited={controlCentreVisited}
            hiddenTooltips={hiddenTooltips}
            handleHideTooltip={handleHideTooltip}
            handleRestoreTooltip={handleRestoreTooltip}
            quietMode={quietMode}
            setQuietModeState={setQuietModeState}
            query={query}
            setQuery={setQuery}
            submitPrimaryQuery={submitPrimaryQuery}
            handleQueryInputChange={handleQueryInputChange}
            queryTextareaRef={queryTextareaRef}
            setShowMenu={setShowMenu}
            primaryCompletions={primaryCompletions}
            primaryTA={primaryTA}
            dataControls={dataControls}
            setLocationConsentPrompt={setLocationConsentPrompt}
            attemptProductSearch={attemptProductSearch}
          />

          {/* D5-6 (2026-02-13) — persistent QuotaDemoBox on the empty home
              state. Prior to this fix, the counter only rendered inside
              the queryThread.map (below), so navigating from Sitemap →
              back reset the counter until a fresh query was fired. Now
              we surface it here too when the server-hydrated
              `billedCount > 0` OR the user has already viewed the Demo
              — i.e. the user is a returning-visitor within this browser
              session, not a fresh landing. */}
          {SHOW_DEMO_COUNTER && queryThread.length === 0 && (billedCount > 0 || demoViewed) && (
            <div className={`mt-4 ${isMobile ? '' : 'flex justify-center'}`} data-testid="empty-state-quota-box">
              <QuotaDemoBox
                used={Math.min(billedCount, 3)}
                quota={3}
                demoViewed={demoViewed}
                onOpenDemo={() => setShowDemoIntro(true)}
                showCCHint={false}
                onOpenCC={() => { markControlCentreVisited(); setShowControlCentre(true); setControlCentreView('main'); fetchDataStats(); }}
                onDismissCC={() => handleHideTooltip('cc_first_query')}
                isMobile={isMobile}
                variant="inline"
              />
            </div>
          )}


          {/* Transact-intent clarification modal (Feb 28, 2026, user-verbatim copy).
              Fires when Enter is hit on a "buy/order/etc X" query without a Source
              chip being clicked. Replaces the old behaviour where the KG path
              would fire and Claude would generate a polite-but-unhelpful refusal. */}
          {/* SPD v5.83 · Phase 2c (2026-07-14) — Location-consent inline
              popup. Fires when the user clicks Support Local or Compare
              while their location is OFF. Y flips dataControls.location
              to 'on' and fires the pending product search; N dismisses.
              Kept intentionally minimal — small tag near the source
              buttons, not a full-screen modal. */}
          {locationConsentPrompt && createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              data-testid="location-consent-popup"
              onClick={(e) => { if (e.target === e.currentTarget) setLocationConsentPrompt(null); }}
            >
              <div
                className="rounded-2xl max-w-sm w-full p-5"
                style={{
                  background: 'linear-gradient(135deg, rgba(30,35,50,0.97) 0%, rgba(15,20,35,0.97) 100%)',
                  border: '1px solid rgba(34,211,238,0.4)',
                }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-100 leading-relaxed">
                      Do you want to provide us permission to turn on your location?
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1 italic">
                      Used only for local retailers within your neighbourhood. Toggle off anytime.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setLocationConsentPrompt(null)}
                    data-testid="location-consent-no"
                    className="px-4 py-1.5 rounded-lg text-sm text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const pending = locationConsentPrompt;
                      setLocationConsentPrompt(null);
                      setDataControls((dc) => ({ ...dc, location: 'on' }));
                      if (pending) attemptProductSearch(pending.query, pending.sources);
                    }}
                    data-testid="location-consent-yes"
                    className="px-4 py-1.5 rounded-lg text-sm font-medium bg-cyan-500 hover:bg-cyan-400 text-slate-900 transition-colors"
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}


          {/* Demo intro modal · extracted 2026-09-01 · IJ-460 · Phase 5B · components/app/DemoIntroModal.jsx */}
          <DemoIntroModal
            showDemoIntro={showDemoIntro}
            setShowDemoIntro={setShowDemoIntro}
            handleRunDemo={handleRunDemo}
          />


          {/* Forecast Loading Indicator */}
          {isForecastLoading && (
            <div className="max-w-4xl mx-auto mb-4 px-6">
              <div className="rounded-xl p-6" style={{ backgroundColor: 'rgba(100, 60, 160, 0.35)', backdropFilter: 'blur(12px)', border: '1px solid rgba(160,100,255,0.2)' }}>
                <div className="flex items-center gap-3">
                  <div className="animate-spin h-6 w-6 border-2 border-purple-400 border-t-transparent rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="text-white font-semibold">Analysing budget data & generating forecast...</p>
                    <p className="text-gray-400 text-sm mt-0.5">This may take up to 60 seconds. The AI is reading your file and performing financial calculations.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Query Thread */}
          {/* Query Thread map — iteration body extracted to
              components/app/QueryThreadItem.jsx in Phase 5E-3f (IJ-254). */}
          {queryThread.map((item, idx) => (
            <QueryThreadItem
              key={idx} item={item} idx={idx}
              isMobile={isMobile} isAdmin={isAdmin}
              SHOW_DEMO_COUNTER={SHOW_DEMO_COUNTER} BACKEND_URL={BACKEND_URL}
              threadEndRef={threadEndRef} followUpRef={followUpRef}
              billedCount={billedCount} demoViewed={demoViewed} isQuerying={isQuerying}
              markControlCentreVisited={markControlCentreVisited} fetchDataStats={fetchDataStats}
              query={query} setQuery={setQuery}
              queryThread={queryThread} setQueryThread={setQueryThread}
              setFollowUpQuery={setFollowUpQuery}
              setShowDemoIntro={setShowDemoIntro}
              setShowControlCentre={setShowControlCentre} setControlCentreView={setControlCentreView}
              selectedFile={selectedFile}
              includeFormulas={includeFormulas} setIncludeFormulas={setIncludeFormulas}
              isForecastLoading={isForecastLoading}
              forecastResult={forecastResult} setForecastResult={setForecastResult}
              forecastTooltip={forecastTooltip} setForecastTooltip={setForecastTooltip}
              showDownloadCoachMark={showDownloadCoachMark} setShowDownloadCoachMark={setShowDownloadCoachMark}
              downloadForecastBtnAnchorRef={downloadForecastBtnAnchorRef}
              feedbackByQid={feedbackByQid}
              commentDraft={commentDraft} setCommentDraft={setCommentDraft}
              submitAnswerFeedback={submitAnswerFeedback}
              browseMode={browseMode} setBrowseMode={setBrowseMode}
              webViewMode={webViewMode} setWebViewMode={setWebViewMode}
              webResultsExpanded={webResultsExpanded} setWebResultsExpanded={setWebResultsExpanded}
              sourcesViewMode={sourcesViewMode} setSourcesViewMode={setSourcesViewMode}
              productResultsExpanded={productResultsExpanded} setProductResultsExpanded={setProductResultsExpanded}
              mediaTab={mediaTab} setMediaTab={setMediaTab}
              mediaCache={mediaCache} setMediaCache={setMediaCache}
              mediaLoading={mediaLoading} setMediaLoading={setMediaLoading}
              mediaError={mediaError} setMediaError={setMediaError}
              switchMediaTab={switchMediaTab}
              setSafeBrowserState={setSafeBrowserState} setLightbox={setLightbox}
              toggleRefineOption={toggleRefineOption} advanceRefinePrompt={advanceRefinePrompt}
              submitRefinedQuery={submitRefinedQuery} skipRefinePrompt={skipRefinePrompt}
              handleQuery={handleQuery} handleCitationClick={handleCitationClick}
              handleDownloadForecast={handleDownloadForecast}
              handleDownloadSourceDocument={handleDownloadSourceDocument}
              handleExportToWord={handleExportToWord} handleGenerateForecast={handleGenerateForecast}
              handleHideTooltip={handleHideTooltip}
              handleInlineForecastUpload={handleInlineForecastUpload}
              handlePermissionResponse={handlePermissionResponse}
              handleProductSearch={handleProductSearch}
              markdownComponents={markdownComponents}
              renderContentWithCitations={renderContentWithCitations}
            />
          ))}

          {/* SPD v5.86 (2026-02-14) · operator directive — the old
              FollowUpInput component is retired in favour of the unified
              QueryInputRow, so the empty next-query input at the bottom
              of the thread now looks IDENTICAL to the primary input at
              the top of the page and to every historical query in the
              thread. Same font size, magnifier icon, typeahead surface,
              submit + chip + freetext callbacks. One source of truth
              for how a query input row behaves. */}
          {queryThread.length > 0 && (
            <QueryInputRow
              value={followUpQuery}
              setValue={setFollowUpQuery}
              inputRef={followUpRef}
              isQuerying={isQuerying}
              typeaheadState={shouldShowChipSurface(followUpQuery) ? followUpTA : null}
              completionsState={followUpCompletions}
              dataTestId="followup"
              onSubmit={() => {
                const q = (followUpQuery || '').trim();
                if (!q) return;
                recordSubmittedQuery(q);
                setFollowUpQuery('');
                handleQuery(q, true);
              }}
              onPickChip={(label) => {
                const cur = (followUpQuery || '').trim();
                const alreadyIncluded = cur.toLowerCase().includes(String(label).toLowerCase());
                const merged = alreadyIncluded ? cur : `${cur} ${label}`.trim();
                const defaultSources = ['local', 'online', 'compare'];
                if (dataControls.location !== 'on') {
                  setFollowUpQuery(merged);
                  setLocationConsentPrompt({ sources: defaultSources, query: merged });
                  return;
                }
                setFollowUpQuery('');
                attemptProductSearch(merged, defaultSources);
              }}
              onPickCompletion={(text) => {
                const defaultSources = ['local', 'online', 'compare'];
                if (dataControls.location !== 'on') {
                  setFollowUpQuery(text);
                  setLocationConsentPrompt({ sources: defaultSources, query: text });
                  return;
                }
                recordSubmittedQuery(text);
                setFollowUpQuery('');
                attemptProductSearch(text, defaultSources);
              }}
              onPickFreetext={(text) => {
                const cur = (followUpQuery || '').trim();
                const alreadyIncluded = cur.toLowerCase().includes(String(text).toLowerCase());
                const merged = alreadyIncluded ? cur : `${cur} ${text}`.trim();
                const defaultSources = ['local', 'online', 'compare'];
                if (dataControls.location !== 'on') {
                  setFollowUpQuery(merged);
                  setLocationConsentPrompt({ sources: defaultSources, query: merged });
                  return;
                }
                setFollowUpQuery('');
                attemptProductSearch(merged, defaultSources);
              }}
            />
          )}

          <div ref={threadEndRef} />
        </div>
      </div>
      
      {/* Floating iceberg-blue back-to-bottom arrow. Anchored to the
          top-right corner of the dark results panel (operator mock
          "Back to bottom button.pdf"). Shows when a result exists and the
          user is not at the bottom; hides while scrolling, returns on stop. */}
      <BackToBottomButton
        enabled={queryThread.length > 0}
        onScrollToBottom={() => {
          if (threadEndRef.current) {
            threadEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          } else {
            window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
          }
        }}
      />

      {/* Floating scroll-follow icons — appears only when the user has
          scrolled past the original top icons row AND has been idle on a
          result for >5 seconds. Slides out immediately on any new scroll
          and reappears once the user settles on a new spot. */}
      <FloatingTopIcons
        originalAnchorRef={topIconsRowRef}
        onOpenCC={() => { markControlCentreVisited(); setShowControlCentre(true); setControlCentreView('main'); fetchDataStats(); }}
        onOpenMenu={() => setShowMenu(true)}
        isMobile={isMobile}
        showCCCallout={false} /* D2.0 follow-up #3 (Feb 23, 2026) — Disabled.
                                 The inline callout anchored to the static
                                 Control Centre icon (App.js ~L2457)
                                 already covers this need; rendering both
                                 caused a visible overlap (see user
                                 screenshot D_Control_Centre_Tag.jpg). The
                                 floating ICONS themselves still appear
                                 for navigation when the user scrolls
                                 past the static row — only the duplicate
                                 callout is removed. */
        onCCCalloutDismiss={() => handleHideTooltip('cc_first_query')}
      />

      {/* Beta investor-campaign exit banner — fires on 4-action completion
          (auto, with 10-min grace) or on Exit click (leave-early, manual).
          Non-blocking; user can still browse the site while it's visible.
          Hidden behind SHOW_BETA_BANNERS flag for enterprise / board-demo posture. */}
      {SHOW_BETA_BANNERS && (
        <SessionExitBanner
          variant={exitBannerVariant}
          onDismiss={() => {
            // D1 review · 2026-02-09: mark session-dismissed BEFORE
            // hiding the banner so the auto-fire useEffect can't re-open
            // it on the same render pass. Grace timer keeps running
            // (see useEffect above — depends on graceStartedAt only).
            bannerDismissedRef.current = true;
            // 2026-02-13 · operator-approved analytics ping. Fire-and-
            // forget with `keepalive: true` so the browser guarantees
            // delivery even if the leave_early variant immediately
            // redirects to LANDING_URL (aborts inflight XHRs otherwise).
            // fail-open: any throw must not break the [Got it] click.
            try {
              const sessionDurationMs = Date.now() - (sessionStartRef.current || Date.now());
              const tasks = Math.min(4, (billedCount || 0) + (demoViewed ? 1 : 0));
              // IJ-215 (2026-08-01) · send the dismissal_token captured
              // from the fire response, NOT the (revoked) bearer JWT.
              // OWASP A01 REMEDIATED · no route accepts a revoked JWT.
              fetch(`${API}/exit-banner/dismissed`, {
                method: 'POST',
                keepalive: true,
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  variant: exitBannerVariant,
                  session_duration_ms: sessionDurationMs,
                  tasks_completed: tasks,
                  dismissal_token: dismissalTokenRef.current || null,
                }),
              }).catch(() => { /* fail-open — analytics must not break UX */ });
            } catch { /* noop */ }
            setExitBannerVariant(null);
          }}
          gracePctRemaining={exitBannerVariant === 'completed' ? gracePctRemaining : undefined}
        />
      )}

      {/* Control Centre - Extracted Component */}
      <ControlCentre
        show={showControlCentre}
        onClose={() => setShowControlCentre(false)}
        onLogout={onLogout}
        view={controlCentreView}
        setView={setControlCentreView}
        accuracyMode={accuracyMode}
        setAccuracyMode={setAccuracyMode}
        accuracyScore={accuracyScore}
        dataControls={dataControls}
        setDataControls={setDataControls}
        saveUserPreferences={saveUserPreferences}
        deleteConfirm={deleteConfirm}
        setDeleteConfirm={setDeleteConfirm}
        handleDeleteClick={handleDeleteClick}
        confirmDelete={confirmDelete}
        dataStats={dataStats}
        dataStatsExpanded={dataStatsExpanded}
        setDataStatsExpanded={setDataStatsExpanded}
        lastDeletedCount={lastDeletedCount}
        showQaPanel={showQaPanel}
        setShowQaPanel={setShowQaPanel}
        qaData={qaData}
        fetchQaSummary={fetchQaSummary}
        runManualQaCheck={runManualQaCheck}
        executeGoNuclear={executeGoNuclear}
        scrubReceipt={scrubReceipt}
        backendBaseUrl={API}
        isMobile={isMobile}
        hiddenTooltips={hiddenTooltips}
        onHideTooltip={handleHideTooltip}
        onRestoreTooltips={handleRestoreTooltips}
        displayUsername={displayUsername}
      />

      {/* Menu - Extracted Component */}
      <MenuOverlay
        show={showMenu}
        onClose={() => setShowMenu(false)}
        view={menuView}
        setView={setMenuView}
        isMobile={isMobile}
        displayUsername={displayUsername}
      />

      {/* Safe Browser - Sandboxed page viewer */}
      {safeBrowserState && (
        <SafeBrowser
          url={safeBrowserState.url}
          title={safeBrowserState.title}
          onClose={() => setSafeBrowserState(null)}
          token={getToken()}
        />
      )}

      {/* Footer */}
      <footer className="relative z-10 py-4 px-3 sm:px-6">
        <div className="container mx-auto flex justify-center sm:justify-end">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 flex-wrap justify-center">
            <button onClick={() => setActivePolicyModal('ethics')} className="hover:text-gray-300 transition-colors" data-testid="ethics-policy-link">Ethics Policy</button>
            <span>·</span>
            <button onClick={() => setActivePolicyModal('terms')} className="hover:text-gray-300 transition-colors" data-testid="terms-of-service-link">Terms of Service</button>
            <span>·</span>
            <button onClick={() => setActivePolicyModal('privacy')} className="hover:text-gray-300 transition-colors" data-testid="privacy-policy-link">Privacy Policy</button>
            <span>·</span>
            <button onClick={() => setActivePolicyModal('aiRisk')} className="hover:text-gray-300 transition-colors" data-testid="ai-risk-policy-link">AI & Data Risk Policy</button>
            <span>·</span>
            <a href="/sitemap" className="hover:text-gray-300 transition-colors" data-testid="sitemap-link">Site Map</a>
          </div>
        </div>
      </footer>

      {/* Bottom overlay stack — extracted to components/app/OverlayStack.jsx in Phase 5G (IJ-256) */}
      <OverlayStack
        citationModal={citationModal}
        setCitationModal={setCitationModal}
        handleDownloadSourceDocument={handleDownloadSourceDocument}
        reindexPrompt={reindexPrompt}
        reindexRunning={reindexRunning}
        reindexProgress={reindexProgress}
        handleReindexConfirm={handleReindexConfirm}
        handleReindexSkip={handleReindexSkip}
        lightbox={lightbox}
        setLightbox={setLightbox}
        activePolicyModal={activePolicyModal}
        setActivePolicyModal={setActivePolicyModal}
        setShowMenu={setShowMenu}
        setMenuView={setMenuView}
      />
    </div>
  );
};

export default KnowledgeGraphPage;
